import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getFeaturedArticles } from '../../../mocks/blog';

export default function FeaturedArticles() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });
  const [activeIndex, setActiveIndex] = useState(0);

  const articles = getFeaturedArticles();

  if (articles.length === 0) return null;

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-28">
      <div className="px-6 lg:px-[7%]">
        <div className="text-center max-w-2xl mx-auto" style={style}>
          <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
            Destacados
          </span>
          <h2 className="font-serif text-brand-black text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.15] mt-4">
            Lecturas
            <span className="italic text-brand-gray-300"> esenciales</span>
          </h2>
        </div>

        {/* Main featured article */}
        <div
          className="mt-16"
          style={isVisible ? {
            opacity: 1,
            transform: 'translateY(0px)',
            transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
          } : {
            opacity: 0,
            transform: 'translateY(36px)',
            transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
            willChange: 'opacity, transform',
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-brand-gray-50">
            <div className="relative h-[320px] md:h-[480px] lg:h-auto">
              <img
                src={articles[activeIndex].image}
                alt={articles[activeIndex].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6">
                <span className="bg-white/90 backdrop-blur-sm text-brand-black font-sans text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full">
                  {articles[activeIndex].category}
                </span>
              </div>
            </div>
            <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-brand-gray-400 text-sm">
                <span>{articles[activeIndex].date}</span>
                <span className="w-1 h-1 rounded-full bg-brand-gray-300" />
                <span>{articles[activeIndex].readTime} de lectura</span>
              </div>
              <h3 className="font-serif text-brand-black text-2xl md:text-[1.75rem] font-semibold leading-[1.3] mt-5">
                {articles[activeIndex].title}
              </h3>
              <p className="font-sans text-brand-gray-500 text-[15px] leading-[1.8] mt-5">
                {articles[activeIndex].excerpt}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {articles[activeIndex].tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-sans text-brand-gray-400 text-xs px-3 py-1.5 bg-brand-gray-100 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/blog/${articles[activeIndex].slug}`}
                className="inline-flex items-center gap-2 font-sans text-brand-black text-[13px] tracking-widest uppercase mt-8 hover:text-brand-gray-500 transition-colors whitespace-nowrap"
              >
                Leer artículo
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        {/* Thumbnail navigation */}
        {articles.length > 1 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {articles.map((article, index) => (
              <button
                key={article.id}
                onClick={() => setActiveIndex(index)}
                className={`text-left rounded-2xl overflow-hidden transition-all duration-300 ${
                  activeIndex === index
                    ? 'ring-2 ring-brand-black'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                <div className="flex gap-4 p-4 bg-brand-gray-50">
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.15em] uppercase">
                      {article.category}
                    </span>
                    <h4 className="font-serif text-brand-black text-sm font-medium leading-[1.4] mt-1 line-clamp-2">
                      {article.title}
                    </h4>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}